import React, { useState, useEffect, useMemo, useRef } from "react";
import { post, get } from "../../services/ApiService";
import toast from "react-hot-toast";
import { useTemplates } from "../Orders/Assessment/hooks/useTemplates";
import { useChatMessages } from "../Orders/Assessment/hooks/useChatMessages";
import TemplateDrawer from "../Orders/Assessment/TemplateDrawer";
import AddTemplateModal from "../Orders/Assessment/TemplateDrawer/AddTemplateModal";
import EditTemplateModal from "../Orders/Assessment/TemplateDrawer/EditTemplateModal";

const ChatAction = ({messages, selectedUser, getMessages, faqs, setFaqs, order, ticket, handleCloseTicket }) => {

  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqVisible, setFaqVisible] = useState(false);
  const [isNoResponse, setIsNoResponse] = useState(false);
  const [hasUnreadMessage, setHasUnreadMessage] = useState(false);
  const [loadingButtons, setLoadingButtons] = useState(false);
    const textareaRef = useRef(null);
      const fileInputRef = useRef(null);

  const { chatMessages, setChatMessages, chatContainerRef } = useChatMessages(messages);
  const { templates, setTemplates, isDragging, onDragStart, onDragEnd } = useTemplates(faqs);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const formatName = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const templateFields = useMemo(
    () => ({
      name: formatName(selectedUser?.name) || "",
      username: selectedUser?.customerId || "",
      address:
        `${selectedUser?.addressLine1},${selectedUser?.addressLine2},${selectedUser?.city},${selectedUser?.zip}` ||
        "",
      postCode: selectedUser?.zip || "",
    }),
    [selectedUser]
  );

  const replaceTemplates = (text) => {
    let result = text;
    Object.entries(templateFields).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
    });
    return result;
  };
  function isValidDate(d) {
    return d instanceof Date && !isNaN(d);
  }

    const handleChange = (e) => {
    setMessage(replaceTemplates(e.target.value));
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
  };

  const handleKeyUp = (e) => {
    if (e.key === "{" || e.key === "}") {
      const cursorPos = textareaRef.current.selectionStart;
      setTimeout(() => {
        textareaRef.current.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    }
  };

  const handleCreateFaq = async (values) => {
    try {
      const res = await post("api/external-log/faqs/add", values);
      setFaqs([...faqs, res.faq]);
      toast.success("FAQ added successfully!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add FAQ");
      return false;
    }
  };

  const handleUpdateFaq = async (id, values) => {
    try {
      const res = await post(`api/external-log/faqs/update/${id}`, values);
      if (res.success) {
        setFaqs(
          faqs.map((faq) => (faq._id === id ? { ...faq, ...values } : faq))
        );
        toast.success("Template updated successfully!");
        return true;
      }
      toast.error(res.error);
      return false;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update FAQ");
      return false;
    }
  };
    // Trigger file input click when attachment button clicked
  const handleAttachmentClick = () => {
    fileInputRef.current.click();
  };

    // Handle file selected from input
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter((file) =>
      ["image/jpeg", "image/png"].includes(file.type)
    );

    if (validImages.length !== files.length) {
      toast.error("Only JPG and PNG images are allowed");
    }

    setAttachments((prev) => [...prev, ...validImages]);
    e.target.value = null;
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!message && message === "") {
      toast.error("Enter your message first");
      return;
    }
    const formData = new FormData();
    formData.append("message", message);
    formData.append("customer", selectedUser.customerId);
    if( order && order._id) {
      formData.append("orderId", order._id);
    } else{
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage._id) {
        formData.append("lastMsgId", lastMessage._id);
      }
    }
    attachments.forEach((file, index) => {
      formData.append("files", file);
    });
    setIsSending(true);
    let res;
    try {
      res = await post(`api/messages/send`, formData, true);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to send message");
      return;
    } finally {
      setIsSending(false);
    }
    if (res.success) {
      toast.success("Message sent");
      setAttachments([]);
      setMessage("");
      if (getMessages) {
        setTimeout(() => {
          getMessages();
        }, 500);
      }
    }
  };

  const handleNoResponse = async () => {
    setIsNoResponse(true);
    const isAmazonThread =
      order?.platform === "Amazon" || (messages || []).some((m) => m.platform === "Amazon");
    // Opened before the await: browsers only allow pop-ups during the click itself.
    const amazonPopup = isAmazonThread
      ? window.open("", "amazonResolveCase", "popup,width=560,height=760")
      : null;
    const sender = selectedUser?.customerId;
    try {
      const body = {
        sender,
        ticketId: ticket?._id,
      };
      const res = await post("api/messages/mark-all-read", body);
      setIsNoResponse(false);
      if (amazonPopup) {
        if (res?.success && res.amazonResolveUrl) {
          amazonPopup.location.href = res.amazonResolveUrl;
        } else {
          amazonPopup.close();
        }
      }
      if (res?.success) {
        if (ticket) {
          handleCloseTicket(res.ticket);
        }
        if (res.amazonResolveUrl && !amazonPopup) {
          toast.error("Pop-up blocked: allow pop-ups for this site, then click No response needed again.");
        } else {
          toast.success(
            res.amazonResolveUrl
              ? "Marked as read. Finish \"No response needed\" in the Amazon window (sign in to Seller Central if asked)."
              : res.message
          );
        }
        window.dispatchEvent(new Event("chat_notifications_updated"));
      } else {
        toast.error("Failed to mark read:", res?.error);
      }
    } catch (error) {
      amazonPopup?.close();
      console.error("Error while read:", error);
      toast.error("Failed to mark read:", error);
      setIsNoResponse(false);
    }
  };



  return (
    <>
    <div className="message-input">
        <textarea
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
          className="form-control"
          rows={5}
          style={{
          width: "100%",
          overflow: "hidden",
          resize: "none",
        }}
          placeholder="Type your message here..."
        />
      </div>
      <div className="chat-actions">
        <button type="button" className="btn btn-primary btn-sm m-4" onClick={handleSendMessage} disabled={isSending}>
          {isSending ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : ( 
          <> Send <i className="fa fa-paper-plane" aria-hidden="true"></i></>
          )}
        </button>
        <button type="button" className="btn btn-secondary btn-sm m-4" onClick={handleAttachmentClick} disabled={isSending}>
          Attach <i className="fa fa-paperclip" aria-hidden="true"></i>
        </button>
        <input
          type="file"
          accept="image/png, image/jpeg"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        <button type="button" className="btn btn-warning btn-sm m-4" onClick={() => setIsTemplateOpen(true)}>
          Templates <i className="fa fa-file-text" aria-hidden="true"></i>
        </button>
        
        <button type="button" className="btn btn-danger btn-sm m-4"
          onClick={() => handleNoResponse()}
          disabled={isNoResponse}
        >
          {isNoResponse ? (
            <>
              Please wait...{" "}
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            </>
          ) : (
            <>
              No response needed{" "}
              <i className="fa fa-times" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
      <div className="col-12 mt-4 d-flex flex-wrap gap-2">
        {attachments.map((file, index) => (
          <div className="attachment-box position-relative" key={index}>
            <img
              src={URL.createObjectURL(file)}
              alt={`attachment-${index}`}
              width={100}
              height={100}
            />
            <span
              className="remove-btn"
              onClick={() => handleRemoveAttachment(index)}
            >
              ×
            </span>
          </div>
        ))}
      </div>

      <TemplateDrawer
        isOpen={isTemplateOpen}
        onClose={() => setIsTemplateOpen(false)}
        templates={templates}
        editMode={editMode}
        setEditMode={setEditMode}
        onDragEnd={onDragEnd}
        isDragging={isDragging}
        onDragStart={onDragStart}
        onTemplateSelect={(content) => setMessage(replaceTemplates(content))}
        onEditTemplate={setEditingFaq}
        onAddTemplate={() => setFaqVisible(true)}
      />

      <AddTemplateModal
        visible={faqVisible}
        onCancel={() => setFaqVisible(false)}
        onSave={handleCreateFaq}
        isSending={isSending}
      />
      <EditTemplateModal
        template={editingFaq}
        onCancel={() => setEditingFaq(null)}
        onSave={handleUpdateFaq}
        isSending={isSending}
      />
    </>
  );
};

export default ChatAction;
